// tslint:disable:jsx-no-lambda
import { Button, Cursor, Icon, Progress, TextInput, VALIDATION_STATE } from '@abb/common-ux';
import { Analysis } from '@module/analysis/Analysis';
import { IPartUploadValidationResult } from '@module/shared';
import { HelpTextProvider } from '@module/shared/components/HelpTextProvider/HelpTextProvider';
import { DownloadTemplateFile } from '@service/DownloadTemplateService';
import { UploadSpareParts } from '@service/UploadSparePartsService';
import download from 'downloadjs';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { IConnectedComponent } from 'reducers';

interface IHomeState {
  stepOneValidationState: VALIDATION_STATE;
  uploadValidationState: VALIDATION_STATE;
  //Flag for -> Users must not be able to collapse non-completed steps
  isFileDownloadedOrSkipped: boolean;
  file: File | null;
  uploadResult: IPartUploadValidationResult | null;
  uploadInProgress: boolean;
  isFileUploaded: boolean;
  isRequestModalOpen: boolean;
}

const EmptyState: IHomeState = {
  stepOneValidationState: 'none',
  uploadValidationState: 'none',
  isFileDownloadedOrSkipped: false,
  file: null,
  uploadResult: null,
  uploadInProgress: false,
  isFileUploaded: false,
  isRequestModalOpen: false
};

/* tslint:disable:no-empty jsx-no-lambda jsx-self-close*/
export class Home extends React.Component<IConnectedComponent, IHomeState> {
  constructor(props: any) {
    super(props);
    this.state = {...EmptyState};
  }

  handleSetUploadValidationState = (validationState: VALIDATION_STATE) => {
    this.setState({ uploadValidationState: validationState });
  }

  handleDownload = async () => {
    let isDownloadSuccessful = true;
    const dispatch = this.props.dispatch;
    try {
      const response = await DownloadTemplateFile();
      const blob = await response.blob();
      download(blob, 'mySpareParts Audit Template.xlsx');
      dispatch({ type: 'BANNER_CLEAR' });
      this.setState({ stepOneValidationState: 'valid' });
    } catch (error) {
      this.setState({ stepOneValidationState: 'invalid' });
      dispatch({ type: 'BANNER_CLEAR' });
      const errorMessage = 'Something went wrong and your download could not be completed.';
      dispatch({ type: 'ADD_BANNER', banners: [{ id: 'fileDownloadError', bannerType: 'error', children: <span>{errorMessage} Try again or contact Customer Support at <a style={{ color: '#0057ff' }} href={`mailto:us-iasvsupport@abb.com?Subject=My%20Spare%20Parts%20Error`} target='_top'>us-iasvsupport@abb.com</a></span> }] });
      isDownloadSuccessful = false;
    }

    if (isDownloadSuccessful) {
      this.setState({ isFileDownloadedOrSkipped: true });
    }
  }

  onFileSelectionChange = async (event: any) => {
    let isUploadSuccessful = true;
    if (event && event.target && event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.setState({ file, uploadResult: null, uploadInProgress: true, uploadValidationState: 'none' });
      const dispatch = this.props.dispatch;
      try {
        // Upload file
        const uploadResult = (await UploadSpareParts(file) as IPartUploadValidationResult);
        if ((uploadResult.Errors && uploadResult.Errors.length > 0) || (!uploadResult.upload)) {
          dispatch({ type: 'BANNER_CLEAR' });
          const errorMessage = uploadResult.Errors ? uploadResult.Errors.join('. ') : `System error: ${uploadResult.status} ${uploadResult.statusText}`;
          dispatch({ type: 'ADD_BANNER', banners: [{ id: 'fileUploadFatalError', bannerType: 'error', children: <span>{errorMessage}</span> }] });
          this.setState({ uploadInProgress: false, uploadValidationState: 'invalid' });
          dispatch({ type: 'UPLOAD_CLEAR' });
          return;
        }
        dispatch({ type: 'BANNER_CLEAR' });
        this.setState({ uploadResult, uploadInProgress: false, uploadValidationState: 'valid' });
        const uploadResultStatus = { TotalParts: uploadResult.UploadResultStatus.TotalParts, MatchedParts: uploadResult.UploadResultStatus.MatchedParts, UnMatchedParts: uploadResult.UploadResultStatus.UnMatchedParts, SparePartsUploadID: uploadResult.upload.sparePartsUploadID };
        dispatch({ type: 'UPLOAD_RESULT', uploadResultStatus });

      } catch (error) {
        dispatch({ type: 'BANNER_CLEAR' });
        const errorMessage = 'Something went wrong and your upload could not be completed.';
        dispatch({ type: 'ADD_BANNER', banners: [{ id: 'fileUploadError', bannerType: 'error', children: <span>{errorMessage} Try again or contact Customer Support at <a style={{ color: '#0057ff' }} href={`mailto:us-iasvsupport@abb.com?Subject=mySpareParts%20Upload%20Error`} target='_top'>us-iasvsupport@abb.com</a></span> }] });
        this.setState({ uploadResult: null, uploadInProgress: false, uploadValidationState: 'invalid' });
        dispatch({ type: 'UPLOAD_CLEAR' });
        isUploadSuccessful = false;
      }
    } else {
      this.setState({ uploadResult: null, uploadValidationState: 'none' });
      isUploadSuccessful = false;
    }
    this.setState({ isFileUploaded: isUploadSuccessful ? true : false });
  }

  onGetFullReportClick = () => {
    this.setState({ isRequestModalOpen: true });
  }

  onCloseHandler = () => {
    this.setState({ isRequestModalOpen: false });
  }

  componentDidUpdate(prevProps: Readonly<IConnectedComponent>, prevState: Readonly<IHomeState>, snapshot?: any): void {
    if (window.location.hash === '#reset') {
      this.setState({...EmptyState});
      this.props.dispatch({ type: 'UPLOAD_CLEAR' });
      window.history.replaceState(null, '', window.location.pathname);
    }
  }

  onFileUploadClick(event: any) {
    // Clear the file input value to allow re-upload of the same file
    event.target.value = null;
    event.target.click();
  }

  // tslint:disable:jsx-no-multiline-js
  render() {
    const certificateDocSVG = require('./UI_CertificateDocument.svg');
    const tableSVG = require('./UI_Table.svg');
    const uploadSVG = require('./UI_Upload.svg');
    const rightArrowSVG = require('./abb_right_32.svg');

    const downloadHelpContent = (
      <ol>
        <li>Select <b>Download</b> button to download mySpareParts Audit Sheet Template File.</li>
        <li>The template is a blank Excel spreadsheet.</li>
        <li>Add spare parts information following instruction and format specified in the template.</li>
        <li>Save the updated file locally with desired file name.</li>
      </ol>
    );
    const uploadHelpContent = (
      <ol>
        <li>Select <b>Upload File</b> button to upload the updated mySpareParts Audit Sheet.</li>
        <li>Browse for and select the saved file.</li>
        <li>If problems occur during file upload, an error with specific reason will appear.  Contact <a href={`mailto:${'us-iasvsupport@abb.com'}`}>{'us-iasvsupport@abb.com'}</a></li>
        <li>Reupload as needed.</li>
        <li>A successful upload is indicated in green.</li>
        <li>Upload time is displayed for the last successful upload.</li>
      </ol>
    );
    let statusElement = <div className='progressBarPosition'><Progress /></div>;
    if (this.state.uploadValidationState !== 'none') {
      const statusIcon = (this.state.uploadValidationState === 'invalid' ? 'error_circle1' : 'check_mark_circle1');
      const statusColor = (this.state.uploadValidationState === 'invalid' ? '#FF0000' : '#008000');
      statusElement = <span><Icon icon={statusIcon} color={statusColor} /></span>;
    }
    if (this.state.uploadInProgress) {
      statusElement = <div className='progressBarPosition'><Progress /></div>;
    }
    let uploadTime = '';
    if (this.state.uploadResult) {
      const uploadDate = this.state.uploadResult.upload.createdAt;
      uploadTime = uploadDate ? '  ' + moment(uploadDate).format('YYYY-MM-DD HH:MM a') : '';
    }
    return (
      <div>
        <div className='row'>
          <div className='col-lg-12'>
            <ol className='breadcrumb breadcrumb_border' style={{ marginBottom: '0px' }}>
              <li className='active' id='workOrderId'>mySpareParts Analyzer</li>
            </ol>
          </div>
        </div>
        <span className='customCursorPosition'>
          <Cursor cursorSize={1} cursorClassName='cursorStyle'>Get Started</Cursor>
          <h6 className='reportDescItem'>Welcome to the ABB mySpareParts Analyzer. Information gathered in this analysis will be used to ensure you have the highest quality ABB spares available and access to effective spares management programs that help prevent downtime and optimize production.</h6>
        </span>
        <div>
          <div className='row step-row'>
            <br />
            <div className='col-sm-3'>
              <div className='row'>
                <div className='col-sm-12' style={{ marginTop: '7px' }}>
                  <div className='getStartedTile first-row-tile'>
                    <div className='row'>
                      <div className='col-sm-12 step-title'>
                        <h3>
                          <b>Step 1: Download Parts Template</b>
                        </h3>
                        <HelpTextProvider modalTitle='Download Instructions' modalContent={downloadHelpContent} render={(onClickHandler: any) => {
                          return <div style={{ cursor: 'pointer', marginTop: '7px' }} onClick={onClickHandler}><Icon icon='help_circle2' size='small' color='#0052FF' /></div>;
                        }} />
                      </div>
                    </div>
                    <label className='btn tile-action-button btn-sm' onClick={this.handleDownload}>
                      Download
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-sm-4'>
              <div className='row'>
                <div className='col-sm-12' style={{ marginTop: '7px' }}>
                  <div className='getStartedTile first-row-tile'>
                    <div className='row'>
                      <div className='col-sm-9 step-title'>
                        <h3>
                          <b>Step 2: Upload Parts List</b>
                        </h3>
                        <HelpTextProvider modalTitle='Upload Instructions' modalContent={uploadHelpContent} render={(onClickHandler: any) => {
                          return <div style={{ cursor: 'pointer', marginTop: '7px' }} onClick={onClickHandler}><Icon icon='help_circle2' size='small' color='#0052FF' /></div>;
                        }} />
                      </div>
                      <div className='col-sm-3'>
                        {this.state.uploadInProgress && statusElement}
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col-sm-3'>
                        <label className='btn tile-action-button btn-sm'>
                          Upload File <input id='btnUploadFile' type='file' style={{ display: 'none' }} accept='.xlsx' onChange={this.onFileSelectionChange} disabled={false} onClick={this.onFileUploadClick} />
                        </label>
                      </div>
                      <div className='col-sm-8'>
                        <TextInput
                          type='text'
                          name=''
                          inputSize='small'
                          readOnly={true}
                          className='no-margin'
                          value={this.state.file && this.state.file.name || ''}
                          errorMessage={'File format incorrect. Please use the provided format'}
                        />
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col-sm-3' />
                      <div className='col-sm-9'>
                        {(uploadTime && this.state.uploadValidationState === 'valid') && (<div><span>{statusElement}</span><span>{' Uploaded: ' + uploadTime}</span></div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-sm-5' style={{ display: this.state.uploadValidationState === 'valid' ? 'block' : 'block' }}>
              <div className='row'>
                <div className='col-sm-12' style={{ marginTop: '7px' }}>
                  <div className='getStartedTile first-row-tile'>
                    <div className='row'>
                      <div className='col-sm-6'>
                    <h3>
                      <b>Step 3: Get the Full Report</b>
                    </h3>
                    </div>
                    <div className='col-sm-6'>
                    <h3>
                      <b>Full Report Includes:</b>
                    </h3>
                    </div>
                    </div>
                    <div className='row'>
                      <div className='col-sm-6'>
                        <Button className='get-full-report-button' onClick={this.onGetFullReportClick} disabled={this.state.uploadValidationState !== 'valid'} buttonType={'primary'} buttonSize={'default'}>Get the Full Report</Button>
                      </div>
                      <div className='col-sm-6'>
                        <div className='row'>
                          <div className='col-sm-6'>
                            <h6 className='reportDescItem'>Lifecycle Phase</h6>
                            <h6 className='reportDescItem'>Sourcing Concerns</h6>
                            <h6 className='reportDescItem'>Repair Risk</h6>
                          </div>
                          <div className='col-sm-6'>
                            <h6 className='reportDescItem'>Replacement Risk</h6>
                            <h6 className='reportDescItem'>Overall Risk</h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <br />
          <div className='row'>
            <div className='col-sm-12'>
              <Analysis isUploadSuccessful={this.state.uploadValidationState === 'valid' ? true : false} isRequestModalOpen={this.state.isRequestModalOpen} onCloseRequestModal={this.onCloseHandler} onGetFullReportClick={this.onGetFullReportClick} />
            </div>
          </div>
          <br />
        </div>
      </div>
    );
  }
}

export default withRouter(connect(mapStateToProps)(Home) as any);

function mapStateToProps(store: any) {
  return {
    loggedInUser: store.userState.loggedInUser
  };
}
